/**
 * BMP Response Interceptor for HttpClient
 *
 * Automatically handles BMP rejection errors (GE401001) across all HTTP requests
 * Converts them to standardized HTTPResponseError for consistent frontend handling
 */

import { HTTPResponseError } from "@lululemon/ecom-site-utils";

/**
 * Response interceptor success handler
 * Checks GraphQL responses for BMP rejection errors
 *
 * @param {AxiosResponse} response - The axios response object
 * @returns {AxiosResponse} The response if no BMP errors found
 * @throws {HTTPResponseError} If BMP rejection detected
 */
const handleSuccessResponse = (response) => {
  // Check for GraphQL errors in successful HTTP responses
  if (response.data?.errors) {
    const bmpError = response.data.errors.find(
      (error) =>
        error?.extensions?.errorCode === "GE401001" ||
        error?.errorCode === "GE401001"
    );

    if (bmpError) {
      throw new HTTPResponseError(
        `BMP rejected request: ${response.config?.url || "unknown"}`,
        {
          responseStatus: response.status,
          errorCode: "GE401001",
          operationName: extractOperationName(response.config),
          isBMPRejection: true,
          originalError: bmpError
        }
      );
    }
  }

  return response;
};

/**
 * Response interceptor error handler
 * Checks HTTP error responses for BMP rejections
 *
 * @param {AxiosError} error - The axios error object
 * @returns {Promise<never>} Always rejects
 * @throws {HTTPResponseError} Standardized BMP error or original error
 */
const handleErrorResponse = (error) => {
  // Check for 400 responses that might contain BMP rejections
  if (error.response?.status === 400) {
    const responseData = error.response.data;

    // Check for BMP error code in response body
    const isBMPRejection =
      responseData?.errorCode === "GE401001" ||
      responseData?.errors?.some?.(
        (err) =>
          err?.extensions?.errorCode === "GE401001" ||
          err?.errorCode === "GE401001"
      );

    if (isBMPRejection) {
      throw new HTTPResponseError(
        `BMP rejected request: ${error.config?.url || "unknown"}`,
        {
          responseStatus: 400,
          errorCode: "GE401001",
          operationName: extractOperationName(error.config),
          isBMPRejection: true,
          originalError: error
        }
      );
    }
  }

  // Re-throw original error if not a BMP rejection
  throw error;
};

/**
 * Extracts GraphQL operation name from axios config
 *
 * @param {AxiosRequestConfig} config - The axios request config
 * @returns {string} The operation name or 'unknown'
 */
const extractOperationName = (config) => {
  try {
    if (config?.data?.operationName) {
      return config.data.operationName;
    }

    if (config?.data?.query) {
      const operationNameRegex =
        /\b(?:query|mutation|subscription)\s+(?:\w+\s+)?(\w+)/;
      const match = config.data.query.match(operationNameRegex);
      return match?.[1] || "unknown";
    }

    return "unknown";
  } catch {
    return "unknown";
  }
};

/**
 * Installs BMP response interceptor on HttpClient instance
 *
 * @param {HttpClient} httpClient - The HttpClient instance
 * @returns {number} The interceptor ID for later removal
 */
export const installBMPResponseInterceptor = (httpClient) => {
  return httpClient.axios.interceptors.response.use(
    handleSuccessResponse,
    handleErrorResponse
  );
};

/**
 * Removes BMP response interceptor from HttpClient instance
 *
 * @param {HttpClient} httpClient - The HttpClient instance
 * @param {number} interceptorId - The ID returned from installation
 */
export const removeBMPResponseInterceptor = (httpClient, interceptorId) => {
  httpClient.axios.interceptors.response.eject(interceptorId);
};
